import * as React from 'react';
import { PieChart } from 'react-minimal-pie-chart';
import { Tooltip } from 'react-tippy';
import ReactSortableTree, { changeNodeAtPath } from 'react-sortable-tree';
import { ApIcon, ApPopover, colors } from '@alixpartners/ui-components';

import 'react-sortable-tree/style.css';

import { getFormattedCurrencyWithIdealDenomination } from 'utils/number';
import {
	lightenColor,
	getNodeKey,
	CREATE_MENU_OPTIONS,
	PROJECT_FIND_MENU_OPTIONS,
	COMPANY_FIND_MENU_OPTIONS,
	CREATE_SIBLING_OPTION,
	MERGE_WITH_ABOVE_OPTION,
	MERGE_WITH_BELOW_OPTION,
} from '../utils';
import { TreeMenu } from '../TreeMenu';
import {
	TreeWrapper,
	ItemWrapper,
	ItemLevel,
	ItemTitle,
	ItemPrice,
	ItemCompanyMarker,
	ItemChartWrapper,
	MoreIconWrapper,
	ItemLevelIconWrapper,
	MappedIconWrapper,
	TitleEditInput,
	TooltipContent,
} from './styles';
import { ChartMenu } from 'TaxonomyMapper/ChartMenu';

const DISABLED_HANDLER = () => false;

export const SortableTree = props => {
	const {
		data: treeData,
		dragDisabled = false,
		treeConfig = {},
		onChange,
		searchQuery,
		selectedTreeItem,
		onTreeItemClick,
	} = props;

	return (
		<TreeWrapper>
			<ReactSortableTree
				treeData={treeData || []}
				onChange={onChange}
				searchMethod={treeSearchMethod}
				searchQuery={searchQuery}
				searchFocusOffset={0}
				canDrop={DISABLED_HANDLER}
				canDrag={
					dragDisabled
						? DISABLED_HANDLER
						: ({ node: { isMapped } }) => !isMapped
				}
				shouldCopyOnOutsideDrop
				dndType={EXTERNAL_NODE_TYPE}
				getNodeKey={getNodeKey}
				rowHeight={44}
				generateNodeProps={nodeData => {
					const { node } = nodeData;
					const style = {};
					if (selectedTreeItem && node.id === selectedTreeItem) {
						style.border = `1px solid ${colors.green1}`;
					}
					if (
						((node.level === 4 && node.children.length === 0) ||
							node.level === 5) &&
						(node.company || node.companyColor)
					) {
						const companyColor = node.company
							? node.company.color
							: node.companyColor;

						style.background = lightenColor(companyColor, 0.05);
						style.borderLeft = `4px solid ${companyColor}`;
					}
					if (onTreeItemClick) {
						style.cursor = 'pointer';
					}
					if (searchQuery) {
						style.opacity = '0.2';
					}

					return {
						title: () => <FormattedTitle {...props} nodeData={nodeData} />,
						style,
						className: node.added ? 'node-added-highlight' : '',
					};
				}}
				{...treeConfig}
			/>
		</TreeWrapper>
	);
};

export const EXTERNAL_NODE_TYPE = 'common';

export const FormattedTitle = props => {
	let {
		currencySymbol,
		editable: originalEditable,
		onChange,
		data: treeData,
		nodeData,
		onMenuOptionSelect,
		onTreeItemClick,
		hideTreeItemChart,
	} = props;
	const { node, path, parentNode } = nodeData;

	const editable = originalEditable && node.level !== 0; // Disable root node from editing
	const isProjectTree = originalEditable;
	const menuOptions = buildMenuOptions(nodeData, isProjectTree);

	let formattedPrice = getFormattedCurrencyWithIdealDenomination({
		value: node.value,
		isUnitSign: true,
	}).toUpperCase();

	formattedPrice = formattedPrice
		? `${currencySymbol}${formattedPrice}`
		: formattedPrice;

	const changeTitleHandler = ({ target: { value } }) => {
		const name = value;

		const updatedTreeData = changeNodeAtPath({
			treeData,
			path,
			getNodeKey,
			newNode: { ...node, name },
		});

		onChange(updatedTreeData);
	};

	const controlsWidth = hideTreeItemChart ? 0 : 140;
	const textRightMargin = 12;
	const titleWidth = getTextWidth(node.name) + textRightMargin;
	const nodeIsLeaf =
		(node.level === 4 && node.children.length === 0) || node.level === 5;
	const nodeProps = onTreeItemClick
		? {
				role: 'button',
				onClick: () => {
					onTreeItemClick(node);
				},
		  }
		: {};
	return (
		<ItemWrapper
			style={editable ? { minWidth: titleWidth + controlsWidth } : {}}
			{...nodeProps}
		>
			{node.isMapped && (
				<MappedIconWrapper>
					<ApIcon
						iconName="outline_done"
						iconSize="24px"
						iconColor={colors.green1}
					/>
				</MappedIconWrapper>
			)}

			{node.level > 0 ? (
				<ItemLevel>
					{node.level <= 4 ? (
						`L${node.level}`
					) : (
						<Tooltip
							position="top"
							distance={8}
							offset={0}
							animation="fade"
							arrow
							html={
								<TooltipContent>
									Level 5 and below items are not <br />
									visible in the taxonomy structure, <br />
									but affect the parent L4 spend value
								</TooltipContent>
							}
							trigger="mouseenter"
						>
							<ItemLevelIconWrapper>
								<ApIcon
									iconName="outline_error"
									iconSize="16px"
									iconColor={colors.grey4}
								/>
							</ItemLevelIconWrapper>
						</Tooltip>
					)}
				</ItemLevel>
			) : null}
			<ItemTitle style={{ width: titleWidth }}>
				{editable ? (
					<TitleEditInput
						key={`${node.level}${node.name}${node.id}`}
						defaultValue={node.name}
						size={Math.max(node.name.length, 10)}
						onBlur={changeTitleHandler}
					/>
				) : (
					node.name
				)}
			</ItemTitle>
			<ItemPrice data={node}>{formattedPrice}</ItemPrice>
			{!hideTreeItemChart &&
				node.chartData && (
					<ItemPieChart data={node.chartData} currencySymbol={currencySymbol} />
				)}
			{!hideTreeItemChart &&
				isProjectTree &&
				nodeIsLeaf && <ItemCompanyMarker color={node.companyColor} />}
			{menuOptions &&
				menuOptions.length > 0 && (
					<MoreIconWrapper>
						<TreeMenu
							onSelect={menuKey =>
								onMenuOptionSelect(menuKey, node, path, parentNode)
							}
							options={menuOptions}
						/>
					</MoreIconWrapper>
				)}
		</ItemWrapper>
	);
};

export const ItemPieChart = ({ data, currencySymbol }) => {
	const ref = React.useRef(null);
	const [isOpen, setOpen] = React.useState(false);
	const toggleOpen = () => setOpen(!isOpen);

	return (
		<React.Fragment>
			<ItemChartWrapper innerRef={ref} onClick={toggleOpen}>
				<PieChart
					data={data.map(dataItem => ({ ...dataItem, title: dataItem.name }))}
					lineWidth={60}
					paddingAngle={8}
					style={{ height: 19 }}
				/>
			</ItemChartWrapper>
			{isOpen && (
				<ApPopover referenceElement={ref.current} onClose={toggleOpen}>
					<ChartMenu data={data} currencySymbol={currencySymbol} />
				</ApPopover>
			)}
		</React.Fragment>
	);
};

const buildMenuOptions = (nodeData, isProjectTree) => {
	const { node, parentNode } = nodeData;
	let menuOptions = [];

	if (isProjectTree && node.level > 0) {
		if (node.level < 3) {
			menuOptions.push(CREATE_MENU_OPTIONS);
		} else if (node.level === 3) {
			menuOptions.push([CREATE_SIBLING_OPTION]);
		}

		if (parentNode && node.level <= 4) {
			const mergeOptions = [];
			const indexInParentNodeChildren = parentNode.children.findIndex(
				item => item.id === node.id
			);
			if (indexInParentNodeChildren > 0) {
				mergeOptions.push(MERGE_WITH_ABOVE_OPTION);
			}
			if (indexInParentNodeChildren < parentNode.children.length - 1) {
				mergeOptions.push(MERGE_WITH_BELOW_OPTION);
			}
			if (mergeOptions.length > 0) {
				menuOptions.push(mergeOptions);
			}
		}

		menuOptions.push(PROJECT_FIND_MENU_OPTIONS);
	} else {
		if (node.isMapped) {
			menuOptions = [COMPANY_FIND_MENU_OPTIONS];
		}
	}

	return menuOptions;
};

const treeSearchMethod = ({ node, searchQuery }) =>
	searchQuery &&
	(searchQuery && Array.isArray(searchQuery)
		? searchQuery.includes(node.id)
		: node.id === searchQuery);

let textWidthCanvas = null;
const getTextWidth = (text, font = 'normal 13px Roboto') => {
	if (!textWidthCanvas) {
		textWidthCanvas = document.createElement('canvas');
		document.body.appendChild(textWidthCanvas);
	}

	const context = textWidthCanvas.getContext('2d');
	context.font = font;
	const metrics = context.measureText(text);
	return metrics.width;
};
